import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CategoryTabs from "@/components/feature/CategoryTabs";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { getNewsByCategory, paginate, getCategoryMeta } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

type SortKey = "latest" | "hot";
type RangeKey = "all" | "7" | "30";
type HotKey = "all" | "hot";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "最多阅读" },
];
const rangeOptions: { key: RangeKey; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "7", label: "近 7 天" },
  { key: "30", label: "近 30 天" },
];
const hotOptions: { key: HotKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "hot", label: "热门" },
];

export default function News() {
  const { category } = useParams();
  const all = getNewsByCategory();
  const refDate = useMemo(
    () => all.reduce((max, item) => (item.date > max ? item.date : max), "1970-01-01"),
    [all],
  );

  const [sort, setSort] = useState<SortKey>("latest");
  const [range, setRange] = useState<RangeKey>("all");
  const [hot, setHot] = useState<HotKey>("all");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const meta = category ? getCategoryMeta(category) : undefined;
  const titleText = category ? `${category}新闻` : "汽车新闻";

  useDocumentMeta({
    title: `汽车资讯网 - ${titleText}`,
    description: meta
      ? `${meta.desc} 汇总${category}相关的最新汽车新闻与市场报道，持续更新。`
      : "汇总轿车、SUV、新能源车、性能跑车等各类别的最新汽车新闻与市场报道。",
    keywords: category
      ? `汽车新闻,${category},汽车资讯`
      : "汽车新闻,轿车新闻,SUV新闻,新能源车新闻,汽车资讯",
  });

  useEffect(() => {
    setPage(1);
  }, [category, sort, range, hot]);

  const filtered = useMemo(() => {
    let list = getNewsByCategory(category);
    if (range !== "all") {
      const days = Number(range);
      const limit = new Date(refDate);
      limit.setDate(limit.getDate() - days);
      const limitStr = limit.toISOString().slice(0, 10);
      list = list.filter((item) => item.date >= limitStr);
    }
    if (hot === "hot") {
      list = list.filter((item) => item.views >= 30000);
    }
    if (sort === "hot") {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return list;
  }, [category, range, hot, sort, refDate]);

  const { items, totalPages, total } = paginate(filtered, page, PAGE_SIZE);

  const reset = () => {
    setSort("latest");
    setRange("all");
    setHot("all");
    setPage(1);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "新闻", to: "/news" }, ...(category ? [{ label: category }] : [])]} />

      <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          {titleText}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">
          {meta ? meta.intro : "汇总汽车行业最新新闻，及时呈现新车上市、产品改款与市场动态，帮助你快速把握车市脉搏。"}
        </p>
      </div>

      <div className="mt-6">
        <CategoryTabs basePath="/news" active={category} allLabel="全部新闻" />
      </div>

      <div className="mt-6 bg-background-50 border border-background-200 rounded-lg">
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-background-200">
          <span className="text-sm text-foreground-600">
            共找到 <strong className="text-primary-700">{total}</strong> 条新闻
          </span>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="md:hidden h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>

        <div
          className={`${filterOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:items-center gap-4 md:gap-8 px-4 md:px-5 py-4 border-b border-background-200`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 mr-1">排序</span>
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSort(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                  sort === opt.key
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 mr-1">时间</span>
            {rangeOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setRange(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                  range === opt.key
                    ? "bg-secondary-600 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 mr-1">热度</span>
            {hotOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setHot(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                  hot === opt.key
                    ? "bg-accent-500 text-primary-950"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 md:p-5">
          {items.length > 0 ? (
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <ArticleCard key={item.id} article={item} variant="row" />
              ))}
            </div>
          ) : (
            <EmptyState
              title="没有找到相关新闻"
              desc="当前分类或筛选条件下暂无内容，点击下方按钮重置筛选条件。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}